import { createEnv } from '@t3-oss/env-core'
import { z } from 'zod'

export const env = createEnv({
	/**
	 * The prefix that client-side variables must have. This is enforced both at
	 * a type-level and at runtime.
	 */
	clientPrefix: 'VITE_',

	client: {
		// API
		VITE_AIR_API_URL: z.string().min(1),
		VITE_AI_API_URL: z.string().min(1),

		// Sentry
		VITE_SENTRY_DSN: z.string(),

		// USE_MOCK_DATA should be treated as a boolean, but .env files only allow strings
		VITE_USE_MOCK_DATA: z
			.string()
			.optional()
			.refine((string) => {
				// Only validate if the value is not undefined
				if (string) {
					return string === 'true' || string === 'false'
				}
				return true
			})
			.transform((string) => string === 'true'),

		// Demo build mode (ADR 0006 decision 2): offline, fully mocked build for
		// the public demo. Set only by `.env.demo` via `pnpm build:demo`.
		VITE_DEMO: z.coerce.boolean<boolean>().default(false),
		VITE_DISABLE_NOTIFICATIONS: z.coerce.boolean<boolean>().default(false),

		// PostHog
		VITE_POSTHOG_KEY: z.string().min(1),
		VITE_POSTHOG_HOST: z.string().min(1),

		// Clerk
		VITE_CLERK_PUBLISHABLE_KEY: z.string().min(1),
		VITE_CLERK_PRECONNECT_URL: z
			.string()
			.optional()
			.default('https://topical-pika-21.clerk.accounts.dev'),

		// Stage detection
		VITE_TARGET_STAGE: z
			.enum(['dev', 'prod', 'local', 'staging'])
			.default('local'),

		// Microsoft Frictionless Onboarding
		VITE_MICROSOFT_FRONTEND_REDIRECT_URL: z
			.url()
			.min(1)
			.default('http://localhost:3001/integrations/new/callback'),

		// END TO END TESTING
		VITE_E2E: z.coerce.boolean<boolean>().default(false),

		// Ephemeral API-capture dev tool (ADR 0007 / issue 01e). Dev-only; the
		// `@/lib/api` guard reads `import.meta.env.VITE_CAPTURE` directly (folded to
		// a literal by `vite.config.ts`) so the tool tree-shakes out of prod.
		VITE_CAPTURE: z.string().optional(),
	},

	/**
	 * What object holds the environment variables at runtime. This is usually
	 * `process.env` or `import.meta.env`.
	 */
	runtimeEnv: import.meta.env,

	/**
	 * By default, this library will feed the environment variables directly to
	 * the Zod validator.
	 *
	 * This means that if you have an empty string for a value that is supposed
	 * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
	 * it as a type mismatch violation. Additionally, if you have an empty string
	 * for a value that is supposed to be a string with a default value (e.g.
	 * `DOMAIN=` in an ".env" file), the default value will never be applied.
	 *
	 * In order to solve these issues, we recommend that all new projects
	 * explicitly specify this option as true.
	 */
	emptyStringAsUndefined: true,

	// Vitest sets MODE='test' (issue 12: replaced VITE_TEST here — its build-time
	// Sentry-skip job moved to VITE_E2E). Under test the app boots with
	// placeholder env, so skip the strict client-var validation real builds enforce.
	skipValidation: import.meta.env.MODE === 'test',
})
